import { describeFiringCadence, describeScenario } from './sound-models.js'

const TAU = Math.PI * 2

function scenarioOrders(model, scenarioName) {
  return model.orders[scenarioName] ?? model.orders.pull ?? model.orders.idle
}

function resonanceColor(model, rpm, sampleIndex, sampleRate) {
  const rf = (rpm - model.engine.idleRpm) / (model.engine.redlineRpm - model.engine.idleRpm)
  let value = 0
  for (const resonance of model.resonances.exhaust) {
    const trackedHz = resonance.frequencyHz * (1 + Math.max(0, rf) * resonance.rpmTrack)
    value += Math.sin((TAU * trackedHz * sampleIndex) / sampleRate) * resonance.gain * 0.045
  }
  return value
}

function cadencePulse(cadence, sampleIndex, sampleRate, rpm) {
  const cycleHz = rpm / 120
  const phase = (cycleHz * sampleIndex) / sampleRate
  const cyclePhase = phase - Math.floor(phase)
  let pulse = 0

  for (let i = 0; i < cadence.offsets.length; i++) {
    const distance = Math.abs(cyclePhase - cadence.offsets[i])
    const wrapped = Math.min(distance, 1 - distance)
    const weight = cadence.weights[i] ?? 1
    pulse += Math.exp(-wrapped * 420) * weight
  }

  return pulse * (0.02 + cadence.cycleSkew * 0.12)
}

function deterministicNoise(sampleIndex, seed = 0) {
  const x = Math.sin((sampleIndex + 1) * 12.9898 + seed * 78.233) * 43758.5453
  return (x - Math.floor(x)) * 2 - 1
}

function combustionTexture(cadence, sampleIndex, sampleRate, scenario) {
  const cycleHz = scenario.rpm / 120
  const phase = (cycleHz * sampleIndex) / sampleRate
  const cyclePhase = phase - Math.floor(phase)
  let ignition = 0
  let gasFlow = 0

  for (let i = 0; i < cadence.offsets.length; i++) {
    const distance = Math.abs(cyclePhase - cadence.offsets[i])
    const wrapped = Math.min(distance, 1 - distance)
    const weight = cadence.weights[i] ?? 1
    ignition += Math.exp(-wrapped * 920) * weight
    gasFlow += Math.exp(-wrapped * 34) * weight
  }

  const noise = deterministicNoise(sampleIndex, cadence.fireCount)
  const previousNoise = deterministicNoise(sampleIndex - 1, cadence.fireCount)
  const flowNoise = deterministicNoise(sampleIndex, cadence.fireCount + 19)
  const highNoise = noise - previousNoise
  const pressure = Math.tanh(ignition * (0.85 + scenario.throttle * 0.42))
  const normalizedFlow = Math.tanh(gasFlow * 0.35)
  const roughness = 0.09 + scenario.throttle * 0.24 + scenario.overrun * 0.72
  const body = pressure * (0.12 + scenario.throttle * 0.16 + cadence.cycleSkew * 0.16)
  const exhaustFlow = normalizedFlow * flowNoise * (0.05 + scenario.throttle * 0.16 + scenario.overrun * 0.18)
  const crackle = highNoise * ignition * roughness

  return body + exhaustFlow + crackle
}

function secondaryEngineLayers(cadence, sampleIndex, sampleRate, scenario) {
  const rotHz = scenario.rpm / 60
  const intakePhase =
    0.5 + 0.5 * Math.sin((TAU * rotHz * Math.max(1, cadence.fireCount / 2) * sampleIndex) / sampleRate)
  const intakeNoise =
    deterministicNoise(sampleIndex, cadence.fireCount + 41) * 0.5 +
    deterministicNoise(sampleIndex - 1, cadence.fireCount + 41) * 0.32 +
    deterministicNoise(sampleIndex - 2, cadence.fireCount + 41) * 0.18
  const intakeGulp = intakeNoise * intakePhase * (0.018 + scenario.throttle * 0.075)
  const valveEdge = Math.max(0, Math.sin((TAU * rotHz * cadence.fireCount * sampleIndex) / sampleRate))
  const mechanicalTick = (Math.pow(valveEdge, 18) - 0.03) * 0.018

  return intakeGulp + mechanicalTick
}

export function renderScenarioSnapshot(model, scenarioName, options = {}) {
  const sampleRate = options.sampleRate ?? 48000
  const durationSeconds = options.durationSeconds ?? 0.25
  const length = Math.max(1, Math.round(sampleRate * durationSeconds))
  const scenario = describeScenario(model, scenarioName)
  const orders = scenarioOrders(model, scenarioName)
  const cadence = describeFiringCadence(model)
  const samples = new Float32Array(length)

  for (let i = 0; i < length; i++) {
    let sample = 0
    for (const entry of orders) {
      const orderHz = (scenario.rpm / 60) * entry.order
      sample += Math.sin((TAU * orderHz * i) / sampleRate) * entry.gain
    }
    sample += cadencePulse(cadence, i, sampleRate, scenario.rpm)
    sample += combustionTexture(cadence, i, sampleRate, scenario)
    sample += secondaryEngineLayers(cadence, i, sampleRate, scenario)
    sample += resonanceColor(model, scenario.rpm, i, sampleRate)
    samples[i] = Math.tanh(sample * 0.42)
  }

  return {
    scenario: scenarioName,
    rpm: scenario.rpm,
    sampleRate,
    durationSeconds,
    samples,
  }
}

export function measureOrderEnergy(samples, sampleRate, rpm, orders) {
  const result = new Map()

  for (const order of orders) {
    const frequencyHz = (rpm / 60) * order
    let re = 0
    let im = 0
    for (let i = 0; i < samples.length; i++) {
      const phase = (TAU * frequencyHz * i) / sampleRate
      re += samples[i] * Math.cos(phase)
      im -= samples[i] * Math.sin(phase)
    }
    result.set(order, Math.hypot(re, im) / samples.length)
  }

  return result
}

function trackedResonancePeaks(model, rpm) {
  const rf = (rpm - model.engine.idleRpm) / (model.engine.redlineRpm - model.engine.idleRpm)
  const channels = [
    ['exhaust', model.resonances.exhaust],
    ['intake', model.resonances.intake],
  ]

  return channels.flatMap(([channel, resonances]) =>
    resonances.map((resonance) => ({
      channel,
      id: resonance.id,
      frequencyHz: resonance.frequencyHz,
      trackedFrequencyHz: resonance.frequencyHz * (1 + Math.max(0, rf) * resonance.rpmTrack),
      q: resonance.q,
      gain: resonance.gain,
    })),
  )
}

function weightedCentroid(orders) {
  const total = orders.reduce((sum, entry) => sum + entry.energy, 0)
  if (total <= 0) return 0
  return orders.reduce((sum, entry) => sum + entry.frequencyHz * entry.energy, 0) / total
}

export function buildScenarioFingerprint(model, scenarioName, options = {}) {
  const snapshot = renderScenarioSnapshot(model, scenarioName, options)
  const scenario = describeScenario(model, scenarioName)
  const cadenceSignature = describeFiringCadence(model)
  const requestedOrders = scenario.dominantOrders.map((entry) => entry.order)
  const energy = measureOrderEnergy(snapshot.samples, snapshot.sampleRate, snapshot.rpm, requestedOrders)
  const orders = scenario.dominantOrders.map((entry) => ({
    order: entry.order,
    frequencyHz: entry.frequencyHz,
    targetGain: entry.gain,
    energy: energy.get(entry.order) ?? 0,
  }))
  const totalOrderEnergy = orders.reduce((sum, entry) => sum + entry.energy, 0)
  const lowOrderEnergy = orders
    .filter((entry) => entry.order <= 1)
    .reduce((sum, entry) => sum + entry.energy, 0)
  const dominantOrder = orders.reduce(
    (best, entry) => (entry.energy > best.energy ? entry : best),
    orders[0] ?? { order: 0, frequencyHz: 0, targetGain: 0, energy: 0 },
  )

  return {
    modelId: model.id,
    label: model.label,
    scenario: scenarioName,
    rpm: snapshot.rpm,
    sampleRate: snapshot.sampleRate,
    durationSeconds: snapshot.durationSeconds,
    eventFocus: scenario.eventFocus,
    throttle: scenario.throttle,
    overrun: scenario.overrun,
    firingHz: scenario.firingHz,
    cadenceSignature,
    orders,
    dominantOrder,
    totalOrderEnergy,
    lowOrderBalance: totalOrderEnergy > 0 ? lowOrderEnergy / totalOrderEnergy : 0,
    spectralCentroidHz: weightedCentroid(orders),
    resonancePeaks: trackedResonancePeaks(model, snapshot.rpm),
    induction: { ...model.forcedInduction },
  }
}

export function compareScenarioFingerprints(left, right) {
  return {
    leftModelId: left.modelId,
    rightModelId: right.modelId,
    scenario: left.scenario === right.scenario ? left.scenario : `${left.scenario}/${right.scenario}`,
    centroidDeltaHz: Math.abs(left.spectralCentroidHz - right.spectralCentroidHz),
    lowOrderBalanceDelta: Math.abs(left.lowOrderBalance - right.lowOrderBalance),
    dominantOrderDelta: Math.abs(left.dominantOrder.order - right.dominantOrder.order),
    totalOrderEnergyRatio:
      Math.max(left.totalOrderEnergy, right.totalOrderEnergy) /
      Math.max(0.000001, Math.min(left.totalOrderEnergy, right.totalOrderEnergy)),
  }
}
